import type { TeamLevelSummaryDto } from '@internal/scorecards-common';
import { mapGroupEntitiesToTeams, useLogger } from '@internal/ui-common';
import { useEffect, useMemo, useState } from 'react';

import type { Entity } from '@backstage/catalog-model';
import { parseEntityRef } from '@backstage/catalog-model';
import { scorecardsApiRef } from '../api/ScorecardsApi';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';

export interface TeamLevelState {
  loading: boolean;
  error?: Error;
  /** GitLab topic of the team, e.g. `team-bhabha`; undefined for non-team groups. */
  team?: string;
  summary?: TeamLevelSummaryDto;
}

/**
 * `Team` strips the division from the group name using `spec.parent`, and
 * expects a bare name (`teams-scalar`). The catalog may hold a full ref
 * (`group:default/teams-scalar`), so reduce it to its name first.
 */
function withBareParent(entity: Entity): Entity {
  const parent = (entity.spec as { parent?: unknown } | undefined)?.parent;
  if (typeof parent !== 'string') return entity;
  try {
    const { name } = parseEntityRef(parent, {
      defaultKind: 'group',
      defaultNamespace: 'default',
    });
    return { ...entity, spec: { ...entity.spec, parent: name } };
  } catch {
    return entity;
  }
}

export function resolveTeamTopic(
  entity: Entity,
  logger: ReturnType<typeof useLogger>,
): string | undefined {
  const slug = mapGroupEntitiesToTeams([withBareParent(entity)], logger)[0]
    ?.nameInGitlab;
  return slug ? `team-${slug}` : undefined;
}

export function useTeamLevel(): TeamLevelState {
  const api = useApi(scorecardsApiRef);
  const { entity } = useEntity();
  const logger = useLogger();
  const team = useMemo(
    () => resolveTeamTopic(entity, logger),
    [entity, logger],
  );

  const [state, setState] = useState<Omit<TeamLevelState, 'team'>>({
    loading: true,
  });

  useEffect(() => {
    if (!team) {
      setState({ loading: false });
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true });

    api
      .getTeamSummary(team)
      .then(summary => {
        if (!cancelled) setState({ loading: false, summary });
      })
      .catch(err => {
        if (!cancelled)
          setState({
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });

    return () => {
      cancelled = true;
    };
  }, [api, team]);

  return { ...state, team };
}
